import { oc } from "@orpc/contract";
import { z } from "zod";
import { dataLabelDraftSchema, dataLabelsSchema } from "@erp/shared-types";

const targetInput = z.object({ id: z.uuid() });
const draftInput = targetInput.extend({ draftId: z.uuid() });
export const labelDraftStateSchema = z.object({
  current: dataLabelsSchema,
  draft: dataLabelDraftSchema.nullable(),
  linkedSourceCount: z.number().int().min(0),
  sampleSources: z.array(z.object({ id: z.uuid(), label: z.string() })),
});
export const labelDraftContract = {
  evidence: oc
    .input(targetInput.extend({ sampleLinkId: z.uuid().optional() }))
    .output(
      z.union([
        z.object({
          fields: z
            .array(
              z.object({
                fieldId: z.uuid(),
                fieldName: z.string(),
                type: z.string(),
                sourceDescription: z.string().nullable(),
                samples: z.array(z.string()).max(3),
              }),
            )
            .max(500),
        }),
        z.object({
          datasets: z.array(
            z.object({ title: z.string(), description: z.string().nullable() }),
          ),
        }),
      ]),
    )
    .route({
      method: "GET",
      summary: "Read evidence for label suggestions",
      description:
        "Returns source field documentation and optionally small samples from a selected authorized data-source link. Does not generate or apply labels.",
    }),
  get: oc.input(targetInput).output(labelDraftStateSchema).route({
    method: "GET",
    summary: "Read accepted labels and the saved draft",
    description:
      "Returns current titles and descriptions, the saved review draft, the number of linked data sources, and available sample links for the target.",
  }),
  start: oc.input(targetInput).output(dataLabelDraftSchema).route({
    method: "POST",
    summary: "Start a saved draft from the current labels",
    description:
      "Creates a review draft from accepted labels without changing them. Rejects the request when another draft already exists.",
  }),
  generate: oc
    .input(
      targetInput.extend({
        sampleLinkId: z.uuid().optional(),
        onlyIfMissing: z.boolean().optional(),
      }),
    )
    .output(dataLabelDraftSchema)
    .route({
      method: "POST",
      summary: "Generate and save suggested labels without applying them",
      description:
        "Suggests titles and descriptions from existing schema metadata and optional samples from a selected linked source. Saves a draft for review while leaving accepted labels unchanged.",
    }),
  save: oc
    .input(draftInput.extend({ labels: dataLabelsSchema }))
    .output(dataLabelDraftSchema)
    .route({
      method: "POST",
      summary: "Edit the saved label draft",
      description:
        "Saves edited titles and descriptions as a new draft revision. Requires the current draft ID and preserves field IDs and raw query names.",
    }),
  accept: oc
    .input(draftInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Apply the reviewed label draft",
      description:
        "Applies the specified current draft only if its accepted metadata baseline is unchanged, then clears the draft. Schema labels update once for all linked data sources without renaming query columns.",
    }),
  discard: oc
    .input(draftInput)
    .output(z.object({ success: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Discard the saved label draft",
      description:
        "Clears the specified current draft without changing accepted titles or descriptions. Rejects stale draft IDs.",
    }),
};
